import React from 'react';

import { useTheme } from '@mui/material/styles';

export type ContainerBreakpoint = "xs" | "sm" | "md" | "lg" | "xl";

/** Context for handling layout data */
export const LayoutContext = React.createContext<{
    containerBp: ContainerBreakpoint;
    updateLayout: (bp: ContainerBreakpoint) => void;
}>({
    containerBp: "md",
    updateLayout: () => {}
});


/**
 * Layout context provider component using forwardRef.
 */
const LayoutHandler = React.forwardRef<Element, { children?: React.ReactNode }>((props, ref) => {
    const { children } = props;
    const theme = useTheme();

    const [containerBp, setContainerBp] = React.useState<ContainerBreakpoint>("md");

    const updateLayout = (bp: ContainerBreakpoint) => {
        setContainerBp(bp);
    };

    // Observe size changes to determine breakpoint
    React.useEffect(() => {
        if (!ref || !(ref as React.RefObject<Element>).current) return;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const width = entry.contentRect.width;
                const { values } = theme.breakpoints;

                // Map width to breakpoint key (adjust logic if you want other thresholds)
                let newBp: ContainerBreakpoint = "xs";
                if (width >= values.lg) newBp = "lg";
                else if (width >= values.md) newBp = "md";
                else if (width >= values.sm) newBp = "sm";
                else newBp = "xs";

                updateLayout(newBp);
            }
        });

        ro.observe((ref as React.RefObject<Element>).current!);
        return () => ro.disconnect();
    }, [theme, ref]);

    return (
        <LayoutContext.Provider value={{ containerBp, updateLayout }}>
            {children}
        </LayoutContext.Provider>
    );
});

export default LayoutHandler;
